// Shared guard for viewport IntersectionObservers against the zero-area lie.
//
// A rendered box with no area reports `isIntersecting: true` with `intersectionRatio: 1` — the
// ratio is not an area quotient — so EVERY observed element clears any threshold at once while it
// has no box. The native apps make that the normal case: both Camping NZ apps call `loadUrl`/
// `load()` into a webview at `size=0x0`, with first layout ~1.7s later (device-measured
// 2026-08-06). An unguarded observer therefore reports everything fully viewed before anything is
// on screen. The website hits the same shape when a component is built before its `<link>`ed
// stylesheet applies (zero WIDTH at a settled viewport).
//
// The guard has two halves, and they are NOT alternatives (see
// .claude/rules/native-webview-unmeasured-load.md):
//
//   1. `observeWhenLaidOut` — defer `.observe()` until the target actually has area (via a
//      ResizeObserver), so the first delivery cannot be the zero-area lie.
//   2. `entryHasArea` — refuse a zero-area entry at fire time, covering a LATER collapse (rotation,
//      split-screen, hidden-then-reshown) that the one-shot defer gate can no longer catch.
//
// Guard on degeneracy, never on smallness: a component measured at a small-but-real size is
// correct, so a "too small" threshold would reject the innocent case and keep the guilty
// degenerate one. Zero area in EITHER axis is the only thing gated on.
//
// See doc/README--zero_area_render_defects.md for the device evidence and case studies, and
// pane_slide.js (the original, from which this was extracted) for the carousel narrative.
//
// For IMPRESSION counting ("was this element seen?"), use `ExposureObserver` in
// exposure_observer.js, which applies both halves of this guard and also decides exposure from
// geometry rather than `isIntersecting`.

// True when the element has real area (non-zero width AND height). Used as the defer-observe gate.
export function hasArea(element) {
    const box = element.getBoundingClientRect();
    return box.width > 0 && box.height > 0;
}

// True when an IntersectionObserver entry describes a box with real area. An entry for a
// rendered box with no area is a lie (ratio 1, empty rect); refuse it at fire time.
export function entryHasArea(entry) {
    const box = entry.boundingClientRect;
    return box.width > 0 && box.height > 0;
}

// Defer `observer.observe(target)` until `target` has real area.
//
//   - target already has area          → observe now, return undefined.
//   - no ResizeObserver support        → `onNoResizeObserver`:
//                                           'observe' (default) → observe now (legacy fallback);
//                                           'skip'              → do nothing (fail closed).
//   - otherwise                        → attach a ResizeObserver to [target, ...alsoWatch]; when
//                                         target has area, disconnect it and observe. Returns the
//                                         ResizeObserver so the caller can register teardown.
//
// `alsoWatch` exists because the ancestor that collapses (the webview) is not always the target
// that last resolves a height: a gate re-checked only on the target's own resizes could wait for
// a resize that never comes. Watch the collapsing ancestor as well as the target.
export function observeWhenLaidOut(observer, target, { alsoWatch = [], onNoResizeObserver = 'observe' } = {}) {
    if (hasArea(target)) {
        observer.observe(target);
        return undefined;
    }

    if (typeof ResizeObserver === 'undefined') {
        if (onNoResizeObserver === 'skip') return undefined;
        observer.observe(target);
        return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
        if (!hasArea(target)) return;
        resizeObserver.disconnect();
        observer.observe(target);
    });
    [target, ...alsoWatch].forEach(element => resizeObserver.observe(element));
    return resizeObserver;
}
